import React, { useState } from 'react';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import { apiService } from '../services/apiService';

const Login = ({ onLogin }) => {
  const [activeTab, setActiveTab] = useState('login'); // 'login' 또는 'signup'
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [signupData, setSignupData] = useState({
    email: '',
    name: '',
    organization: '',
    reason: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // 비밀번호 변경 챌린지 처리를 위한 state
  const [passwordChallenge, setPasswordChallenge] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmChallengePassword, setConfirmChallengePassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmChallengePassword, setShowConfirmChallengePassword] = useState(false);

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
    // 입력시 에러 메시지 제거
    if (error) setError('');
  };

  const handleSignupChange = (e) => {
    setSignupData({
      ...signupData,
      [e.target.name]: e.target.value
    });
    // 입력시 메시지 제거
    if (error) setError('');
    if (successMessage) setSuccessMessage('');
  };
  
  const handleNewPasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmChallengePassword) {
      setError('새 비밀번호를 입력해주세요.');
      return;
    }
    
    if (newPassword !== confirmChallengePassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('비밀번호는 8자 이상이어야 합니다.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // authService에서 setNewPassword 메서드 호출
      const { authService } = await import('../services/authService');
      const result = await authService.setNewPassword(
        passwordChallenge.session,
        newPassword,
        passwordChallenge.username
      );
      
      if (result.success) {
        // 새 비밀번호 설정 성공 후 자동 로그인
        window.location.reload();
      } else {
        setError('새 비밀번호 설정에 실패했습니다.');
      }
    } catch (error) {
      setError(error.message || '새 비밀번호 설정 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!credentials.username || !credentials.password) {
      setError('사용자명과 비밀번호를 입력해주세요.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await onLogin(credentials);
      if (result.success) {
        // 로그인 성공
        return;
      } else if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        // 새 비밀번호 설정 필요
        setPasswordChallenge({
          session: result.session,
          username: credentials.username,
          message: result.message
        });
      } else {
        setError(result.error || '로그인에 실패했습니다.');
      }
    } catch (error) {
      setError(error.message || '로그인 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    // 폼 검증 (비밀번호 필드 제거)
    if (!signupData.email || !signupData.name || !signupData.organization) {
      setError('모든 필수 항목을 입력해주세요.');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccessMessage('');
    
    try {
      // 신청서 제출 API 호출 (apiService 사용)
      const result = await apiService.post('/user-registration', {
        email: signupData.email,
        name: signupData.name,
        organization: signupData.organization,
        reason: signupData.reason || ''
      });
      
      // apiService는 자동으로 응답을 처리하므로 성공시 result가 반환됨
      setSuccessMessage('가입 신청이 성공적으로 제출되었습니다. 관리자 승인 후 이메일로 로그인 정보를 안내드리겠습니다.');
      // 성공 시 로그인 탭으로 전환
      setTimeout(() => {
        setActiveTab('login');
        setSignupData({
          email: '',
          name: '',
          organization: '',
          reason: ''
        });
        setSuccessMessage('');
      }, 5000);
    } catch (error) {
      console.error('신청서 제출 오류:', error);
      setError('신청서 제출 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
            <LogIn className="h-6 w-6 text-blue-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            AWS Resource Control
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            EC2 리소스를 효율적으로 관리하세요
          </p>
        </div>

        {/* 탭 메뉴 */}
        {!passwordChallenge && (
          <div className="flex border-b border-gray-200">
            <button
              type="button"
              onClick={() => {
                setActiveTab('login');
                setError('');
                setSuccessMessage('');
              }}
              className={`flex-1 py-2 px-1 text-center border-b-2 font-medium text-sm ${
                activeTab === 'login'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              로그인
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('signup');
                setError('');
                setSuccessMessage('');
              }}
              className={`flex-1 py-2 px-1 text-center border-b-2 font-medium text-sm ${
                activeTab === 'signup'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              가입 신청
            </button>
          </div>
        )}
        
        {!passwordChallenge ? (
          activeTab === 'login' ? (
          // 일반 로그인 폼
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                사용자명 또는 이메일
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                required
                value={credentials.username}
                onChange={handleChange}
                className="mt-1 relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="사용자명 또는 이메일을 입력하세요"
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                비밀번호
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={credentials.password}
                  onChange={handleChange}
                  className="relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="비밀번호를 입력하세요"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  로그인 중...
                </div>
              ) : (
                '로그인'
              )}
            </button>
          </div>

          <div className="text-center">
            <p className="text-xs text-gray-500">
              AWS Cognito 인증을 통해 안전하게 로그인됩니다
            </p>
          </div>
        </form>
        ) : (
          // 회원가입 폼
          <form className="mt-8 space-y-6" onSubmit={handleSignup}>
            <div className="space-y-4">
              <div>
                <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700">
                  이메일 주소 *
                </label>
                <input
                  id="signup-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={signupData.email}
                  onChange={handleSignupChange}
                  className="mt-1 relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="이메일 주소를 입력하세요"
                />
              </div>

              <div>
                <label htmlFor="signup-name" className="block text-sm font-medium text-gray-700">
                  이름
                </label>
                <input
                  id="signup-name"
                  name="name"
                  type="text"
                  value={signupData.name}
                  onChange={handleSignupChange}
                  className="mt-1 relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="이름을 입력하세요 (선택사항)"
                />
              </div>

              <div>
                <label htmlFor="signup-organization" className="block text-sm font-medium text-gray-700">
                  소속/조직 *
                </label>
                <input
                  id="signup-organization"
                  name="organization"
                  type="text"
                  required
                  value={signupData.organization}
                  onChange={handleSignupChange}
                  className="mt-1 relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="소속이나 조직을 입력하세요"
                />
              </div>

              <div>
                <label htmlFor="signup-reason" className="block text-sm font-medium text-gray-700">
                  신청 사유
                </label>
                <textarea
                  id="signup-reason"
                  name="reason"
                  rows={3}
                  value={signupData.reason}
                  onChange={handleSignupChange}
                  className="mt-1 relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm resize-none"
                  placeholder="시스템 사용 목적이나 신청 사유를 입력하세요 (선택사항)"
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            {successMessage && (
              <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md text-sm">
                {successMessage}
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    신청서 제출 중...
                  </div>
                ) : (
                  '가입 신청하기'
                )}
              </button>
            </div>

            <div className="text-center">
              <p className="text-xs text-gray-500">
                신청서 제출 후 관리자 승인을 거쳐 계정이 생성됩니다
              </p>
            </div>
          </form>
        )
        ) : (
          // 새 비밀번호 설정 폼
          <form className="mt-8 space-y-6" onSubmit={handleNewPasswordSubmit}>
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-md text-sm mb-4">
              {passwordChallenge.message}
            </div>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                  새 비밀번호
                </label>
                <div className="mt-1 relative">
                  <input
                    id="newPassword"
                    name="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    required
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      if (error) setError('');
                    }}
                    className="relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                    placeholder="8자 이상, 대소문자, 숫자, 특수문자 포함"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>
              
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  비밀번호 확인
                </label>
                <div className="mt-1 relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmChallengePassword ? 'text' : 'password'}
                    required
                    value={confirmChallengePassword}
                    onChange={(e) => {
                      setConfirmChallengePassword(e.target.value);
                      if (error) setError('');
                    }}
                    className="relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                    placeholder="비밀번호를 다시 입력하세요"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmChallengePassword(!showConfirmChallengePassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showConfirmChallengePassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="space-y-3">
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    설정 중...
                  </div>
                ) : (
                  '비밀번호 설정'
                )}
              </button>
              
              <button
                type="button"
                onClick={() => {
                  setPasswordChallenge(null);
                  setNewPassword('');
                  setConfirmChallengePassword('');
                  setError('');
                }}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                뒤로 가기
              </button>
            </div>

            <div className="text-center">
              <p className="text-xs text-gray-500">
                첫 로그인 시 새 비밀번호를 설정해야 합니다
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;